import { Spin } from '@honestica/ui-kit/src';
import {
  CaretDownOutlinedIcon,
  CheckCircleTwoToneIcon,
  CloseOutlinedIcon,
  ExclamationCircleTwoToneIcon,
  HourglassTwoToneIcon,
  LoadingOutlinedIcon,
  WarningTwoToneIcon,
} from '@honestica/ui-kit/src/icons';
import colors from '@honestica/ui-kit/src/theme/colors';
import { UploadError, UploadStatus } from '@launcher/context/DocumentProcessing.context';
import { tracking } from '@services/analytics';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentImportCard } from './DocumentImportCard.component';
import { DocumentImportSectionHeader } from './DocumentImportSectionHeader.component';
import styles from './DocumentImportStatus.module.less';

export interface DocumentProgress {
  id: string;
  key?: string; // Optional stable key for React
  name: string;
  status: UploadStatus;
  error?: UploadError;
  children?: DocumentProgress[];
  totalChildren?: number; // Total number of children (including completed)
  parentName?: string;
}

export interface DocumentImportStatusProps {
  documents: DocumentProgress[];
  onExpandChange?: (expanded: boolean) => void;
  onClose?: () => void;
}

const documentKey = (doc: DocumentProgress) => doc.key || doc.id;

// A single flat array drives a single React.map() call, so every card and header shares one
// reconciliation key-space. That's what lets a card move from one section to another (a change
// of position within this array) without React unmounting and remounting it: two separate
// {arr.map(...)} expressions placed side by side in JSX look flattened visually, but each is
// reconciled as its own list, so a key moving between them would still remount.
type Row =
  | { type: 'header'; key: string; title: string; count: number; icon: ReactNode; testId: string }
  | { type: 'card'; key: string; doc: DocumentProgress; order: number };

export const DocumentImportStatus = ({
  documents,
  onExpandChange,
  onClose,
}: DocumentImportStatusProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const orderRef = useRef(new Map<string, number>());

  const getOrder = (doc: DocumentProgress) => {
    const key = documentKey(doc);
    if (!orderRef.current.has(key)) {
      orderRef.current.set(key, orderRef.current.size);
    }
    return orderRef.current.get(key)!;
  };

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onExpandChange?.(newExpandedState);

    if (newExpandedState) {
      tracking.importModalExpanded({ totalDocuments: documents.length });
    }
  };

  const importCount = documents.length;
  const completedCount = documents.filter(
    (doc) =>
      doc.status === 'TERMINATED' ||
      doc.status === 'FAILURE' ||
      doc.status === 'ANALYZE_UNAVAILABLE',
  ).length;

  const hasErrors = documents.some((doc) => doc.status === 'FAILURE');
  const isLoading = documents.some(
    (doc) => doc.status === 'UPLOADING' || doc.status === 'ANALYZING',
  );
  const hasSuccess = documents.some(
    (doc) => doc.status === 'TERMINATED' || doc.status === 'ANALYZE_UNAVAILABLE',
  );

  // Error takes priority - only show error if there are errors, otherwise show success
  const showErrorIcon = hasErrors;
  const showSuccessIcon = !isLoading && !hasErrors && hasSuccess;
  const showCloseButton = !isLoading && completedCount === importCount;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    tracking.importModalClosed();
    onClose?.();
  };

  // Group documents by status, keeping a stable per-document animation ordinal
  const withOrder = documents.map((doc) => ({ doc, order: getOrder(doc) }));
  const pick = (...statuses: UploadStatus[]) =>
    withOrder.filter(({ doc }) => statuses.includes(doc.status)).sort((a, b) => a.order - b.order);

  const errorDocuments = pick('FAILURE');
  const analyzeUnavailableDocuments = pick('ANALYZE_UNAVAILABLE');
  const progressDocuments = pick('PENDING', 'UPLOADING', 'ANALYZING');
  const successDocuments = pick('TERMINATED');

  const sections = [
    {
      key: 'errors',
      title: t('documents.importStatus.section.errors'),
      icon: <ExclamationCircleTwoToneIcon twoToneColor={colors.red[4]} />,
      testId: 'document-import-section-errors',
      docs: errorDocuments,
    },
    {
      key: 'analyze-unavailable',
      title: t('documents.importStatus.section.analyzeUnavailable'),
      icon: <WarningTwoToneIcon twoToneColor={colors.volcano[5]} />,
      testId: 'document-import-section-analyze-unavailable',
      docs: analyzeUnavailableDocuments,
    },
    {
      key: 'progress',
      title: t('documents.importStatus.section.progress'),
      icon: <HourglassTwoToneIcon twoToneColor={colors.blue[6]} />,
      testId: 'document-import-section-progress',
      docs: progressDocuments,
    },
    {
      key: 'success',
      title: t('documents.importStatus.section.success'),
      icon: <CheckCircleTwoToneIcon twoToneColor={colors.green[5]} />,
      testId: 'document-import-section-success',
      docs: successDocuments,
    },
  ];

  const rows: Row[] = sections.flatMap((section) => [
    ...(section.docs.length > 0
      ? [
          {
            type: 'header' as const,
            key: `header-${section.key}`,
            title: section.title,
            count: section.docs.length,
            icon: section.icon,
            testId: section.testId,
          },
        ]
      : []),
    ...section.docs.map(
      ({ doc, order }) =>
        ({ type: 'card' as const, key: documentKey(doc), doc, order }) satisfies Row,
    ),
  ]);

  return (
    <div
      className={styles.container}
      data-expanded={isExpanded}
      data-testid="document-import-status"
    >
      <div
        className={styles.header}
        onClick={handleToggle}
        data-testid="document-import-status-header"
      >
        <div className={styles.headerTitle}>{t('documents.importStatus.title')}</div>
        <div className={styles.headerActions}>
          <CaretDownOutlinedIcon
            className={styles.headerIcon}
            data-testid="document-import-status-toggle"
            style={{
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transformOrigin: 'center',
            }}
          />
          {showCloseButton && (
            <CloseOutlinedIcon
              className={styles.closeIcon}
              onClick={handleClose}
              data-testid="document-import-status-close"
            />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.counter} data-testid="document-import-status-counter">
          <span className={styles.description}>
            {t('documents.importStatus.description', { completedCount, importCount })}
          </span>
          {showErrorIcon && (
            <ExclamationCircleTwoToneIcon
              twoToneColor={colors.red[4]}
              style={{ fontSize: '24px' }}
              data-testid="document-import-status-icon-error"
            />
          )}
          {isLoading && (
            <Spin
              indicator={<LoadingOutlinedIcon spin style={{ width: '24px', height: '24px' }} />}
              data-testid="document-import-status-icon-loading"
            />
          )}
          {showSuccessIcon && (
            <CheckCircleTwoToneIcon
              twoToneColor={colors.green[5]}
              style={{ fontSize: '24px' }}
              data-testid="document-import-status-icon-success"
            />
          )}
        </div>
      </div>

      <div
        className={styles.expandedContent}
        data-visible={isExpanded}
        data-testid="document-import-status-expanded-content"
      >
        {rows.map((row) =>
          row.type === 'header' ? (
            <DocumentImportSectionHeader
              key={row.key}
              title={row.title}
              count={row.count}
              icon={row.icon}
              testId={row.testId}
            />
          ) : (
            <DocumentImportCard key={row.key} document={row.doc} index={row.order} />
          ),
        )}
      </div>

      {isExpanded && (
        <div className={styles.footer} data-testid="document-import-status-footer">
          <p>{t('documents.importStatus.footer')}</p>
        </div>
      )}
    </div>
  );
};
